import { isEmpty } from 'lodash';
import { createStore } from './createStore';

const actionContainerStoreConfig = {
  id: 'actionContainer',
  initialState: {
    // Editor and template states
    isEditMode: true,
    template: null,
    placeholders: [],
    // Users states
    ccUsers: null,
    bccUsers: null,
    // UI states
    submitActionLoading: null,
    showCc: null,
    showBcc: null,
    isUploading: null,
    isDisabled: null,
    // Mailbox states
    mailboxes: null,
    selectedMailbox: null,
    popoverActive: null,
    // Note states
    isPublicNote: null,
    // Editor UI states
    editorWidthIsSmall: null,
    hideToolbar: null,
    isBannerVisible: null,
    // Private channel states
    isPrivateChannel: null,
    recipients: null
  },
  methods: (set, get) => ({
    initialize: (props) => {
      const allData = get();
      const {
        isEditMode,
        placeholders,
        submitActionLoading,
        isUploading,
        isDisabled,
        popoverActive,
        isPublicNote,
        editorWidthIsSmall,
        hideToolbar,
        isBannerVisible,
        isPrivateChannel
      } = allData;
      set({
        placeholders: placeholders || props?.placeholders || [],
        submitActionLoading: false,
        isUploading: false,
        isDisabled: false,
        popoverActive: false,
        isPublicNote: isPublicNote ?? !!props?.isPublicNote,
        editorWidthIsSmall: editorWidthIsSmall ?? !!props?.editorWidthIsSmall,
        hideToolbar: hideToolbar ?? !!props?.hideToolbar,
        isBannerVisible: isBannerVisible ?? !!props?.isBannerVisible,
        isPrivateChannel: isPrivateChannel ?? !!props?.isPrivateChannel
      });
    },

    initializeTemplate: (template) => {
      const allData = get();
      set({ template: allData?.template || template });
    },

    initializeRecipients: (props) => {
      const allData = get();
      set({
        ccUsers: allData?.ccUsers || props?.ccUsers,
        bccUsers: allData?.bccUsers || props?.bccUsers,
        showCc: allData?.showCc ?? props?.showCc,
        showBcc: allData?.showBcc ?? props?.showBcc,
        selectedMailbox: allData?.selectedMailbox ?? props?.selectedMailbox,
        mailboxes: allData?.mailboxes || props?.mailboxes || [],
        recipients: allData?.recipients || props?.recipients
      });
    },
    setData: (data) => {
      set({ data: data });
    },
    // Editor and template methods
    setIsEditMode: (isEditMode) => {
      set({ isEditMode });
    },
    setTemplate: (template) => {
      set({ template });
    },
    updateTemplate: (templateUpdates) => {
      const currentTemplate = get().template;
      set({ template: { ...currentTemplate, ...templateUpdates } });
    },
    setPlaceholders: (placeholders) => {
      set({ placeholders });
    },
    // Users methods
    setCcUsers: (ccUsers) => {
      set({ ccUsers });
    },
    setBccUsers: (bccUsers) => {
      set({ bccUsers });
    },
    // UI methods
    setSubmitActionLoading: (submitActionLoading) => {
      set({ submitActionLoading });
    },
    setShowCc: (showCc) => {
      set({ showCc });
    },
    setShowBcc: (showBcc) => {
      set({ showBcc });
    },
    setIsUploading: (isUploading) => {
      set({ isUploading });
    },
    setIsDisabled: (isDisabled) => {
      set({ isDisabled });
    },
    // Mailbox methods
    setMailboxes: (mailboxes) => {
      set({ mailboxes });
    },
    setSelectedMailbox: (selectedMailbox) => {
      set({ selectedMailbox });
    },
    setPopoverActive: (popoverActive) => {
      set({ popoverActive });
    },
    // Note methods
    setIsPublicNote: (isPublicNote) => {
      set({ isPublicNote });
    },
    // Editor UI methods
    setEditorWidthIsSmall: (editorWidthIsSmall) => {
      set({ editorWidthIsSmall });
    },
    setHideToolbar: (hideToolbar) => {
      set({ hideToolbar });
    },
    setIsBannerVisible: (isBannerVisible) => {
      set({ isBannerVisible });
    },
    setRecipients: (recipients) => {
      set({ recipients });
    },
    // Reset method to clear all states
    resetStore: () => {
      set({
        data: {},
        isEditMode: true,
        template: {
          name: '',
          content: '',
          placeholdersUsed: '',
          mentions: [],
          attachments: [],
          inlineImages: []
        },
        placeholders: [],
        ccUsers: [],
        bccUsers: [],
        submitActionLoading: false,
        showCc: false,
        showBcc: false,
        isUploading: false,
        isDisabled: false,
        mailboxes: [],
        selectedMailbox: null,
        popoverActive: false,
        isPublicNote: false,
        editorWidthIsSmall: false,
        hideToolbar: true,
        isBannerVisible: false
      });
    },
    setIsPrivateChannel: (isPrivateChannel) => {
      set({ isPrivateChannel });
    }
  }),
  cache: {
    expiryTime: 300,
    strategy: 'memory',
    tabBehavior: 'persist',
    clearOnRefresh: true
  }
};

// Store instance cache to prevent creating multiple instances for the same component
const storeInstances = new Map();

// Function to create or get existing store instance
const createActionContainerStoreInstance = (
  componentId,
  isGlobal = false,
  cacheConfig
) => {
  const cacheKey = `${componentId}_${isGlobal ? 'global' : 'regular'}`;

  if (storeInstances.has(cacheKey)) {
    return storeInstances.get(cacheKey);
  }

  const config = {
    ...actionContainerStoreConfig,
    id: `actionContainer_${componentId}`
  };

  if (cacheConfig?.scope === 'global') {
    config.cache.scope = 'global';
  }

  const storeInstance = createStore(config);
  storeInstances.set(cacheKey, storeInstance);
  return storeInstance;
};

export const useActionContainerStore = createStore(actionContainerStoreConfig);

// Custom hook to return appropriate store based on props
export const useGlobalActionContainerStore = (props = {}) => {
  const { componentId, ticketId, cacheConfig } = props;

  // Create component id using ticket id with 'new' suffix
  const finalComponentId =
    componentId || (ticketId ? `${ticketId}_new` : 'default');

  // For regular components, create/get unique instance based on componentId
  if (finalComponentId) {
    return createActionContainerStoreInstance(
      finalComponentId,
      false,
      cacheConfig
    );
  }

  // Fallback to default store for backward compatibility
  return useActionContainerStore;
};
